import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { Loader, FileIconLink } from 'ui-kit';
import { fetchConstructor } from '_entities/old/api/api';
import URLS from '_constants/URLS';
import * as Styled from './styles';

const PAGE_SIZE = 10;
const VISIT_TYPE_TO_PATH = {
  1: URLS.enforcement,
  2: URLS.education,
  3: URLS.assessments,
};

const visitDetailUrl = (visit) => {
  const base = VISIT_TYPE_TO_PATH[visit.visit_type] || URLS.enforcement;
  return `${base}/${visit.visit_id}`;
};

const absoluteImageUrl = (url) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/')) return `${window.location.origin}${url}`;
  return url;
};

const RetailerImagesTab = ({ retailerId }) => {
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [results, setResults] = useState([]);
  const [activeImage, setActiveImage] = useState(null);
  const [filter, setFilter] = useState('all');

  const totalPages = useMemo(() => Math.ceil(count / PAGE_SIZE) || 1, [count]);

  const fetchPage = useCallback(
    async (targetPage, currentFilter) => {
      setLoading(true);
      try {
        const token = sessionStorage.getItem('token');
        const headers = { Authorization: `Token ${token}` };
        const filterParam =
          currentFilter === 'images' ? '&only_images=t' : currentFilter === 'docs' ? '&only_docs=t' : '';
        const url = `retailers/${retailerId}/media?page=${targetPage}&page_size=${PAGE_SIZE}${filterParam}`;
        const response = await fetchConstructor('get', url, headers);
        if (response && response.status === 200) {
          setResults(response.data.results || []);
          setCount(response.data.count || 0);
        } else {
          setResults([]);
          setCount(0);
        }
      } finally {
        setLoading(false);
      }
    },
    [retailerId],
  );

  useEffect(() => {
    fetchPage(page, filter);
  }, [fetchPage, page, filter]);

  const handleFilterChange = (next) => {
    if (next === filter) return;
    setPage(1);
    setFilter(next);
  };

  const closeModal = useCallback(() => setActiveImage(null), []);

  const switcher = (
    <Styled.Switcher>
      <button
        type="button"
        className={filter === 'all' ? 'active' : ''}
        onClick={() => handleFilterChange('all')}
        disabled={loading}
      >
        All
      </button>
      <button
        type="button"
        className={filter === 'images' ? 'active' : ''}
        onClick={() => handleFilterChange('images')}
        disabled={loading}
      >
        Images
      </button>
      <button
        type="button"
        className={filter === 'docs' ? 'active' : ''}
        onClick={() => handleFilterChange('docs')}
        disabled={loading}
      >
        Documents
      </button>
    </Styled.Switcher>
  );

  if (loading) {
    return (
      <Styled.Wrapper>
        {switcher}
        <Loader />
      </Styled.Wrapper>
    );
  }

  if (!results.length) {
    return (
      <Styled.Wrapper>
        {switcher}
        <Styled.Empty>No items</Styled.Empty>
      </Styled.Wrapper>
    );
  }

  return (
    <Styled.Wrapper>
      {switcher}
      <Styled.Table>
        <thead>
          <tr>
            <Styled.Th width="220px">Visit date / time</Styled.Th>
            <Styled.Th>Images</Styled.Th>
          </tr>
        </thead>
        <tbody>
          {results.map((row) => (
            <tr key={row.visit_id}>
              <Styled.TdDate>
                <Link to={visitDetailUrl(row)}>
                  {row.visit_time ? moment(row.visit_time).format('MM/DD/YYYY HH:mm') : '—'}
                </Link>
              </Styled.TdDate>
              <Styled.TdImages>
                <Styled.Strip>
                  {(row.media || []).map((item) =>
                    item.kind === 'image' ? (
                      <Styled.Thumb
                        key={item.id}
                        type="button"
                        onClick={() => setActiveImage(item)}
                        title={item.file_name}
                      >
                        <img src={absoluteImageUrl(item.url)} alt={item.file_name} />
                      </Styled.Thumb>
                    ) : (
                      <FileIconLink
                        key={item.id}
                        value={{ file_name: item.file_name, file_path: absoluteImageUrl(item.url) }}
                      />
                    ),
                  )}
                </Styled.Strip>
              </Styled.TdImages>
            </tr>
          ))}
        </tbody>
      </Styled.Table>

      {totalPages > 1 && (
        <Styled.Pagination>
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Prev
          </button>
          <span>
            Page {page} / {totalPages}
          </span>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </button>
        </Styled.Pagination>
      )}

      {activeImage && (
        <Styled.ModalBackdrop onClick={closeModal}>
          <Styled.ModalContent onClick={(e) => e.stopPropagation()}>
            <Styled.CloseButton type="button" onClick={closeModal}>
              ×
            </Styled.CloseButton>
            <img src={absoluteImageUrl(activeImage.url)} alt={activeImage.file_name} />
            <Styled.ModalCaption>{activeImage.file_name}</Styled.ModalCaption>
          </Styled.ModalContent>
        </Styled.ModalBackdrop>
      )}
    </Styled.Wrapper>
  );
};

RetailerImagesTab.propTypes = {
  retailerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default RetailerImagesTab;
